import React, { useState, useRef } from 'react';
import { Upload, Download, FileText, AlertCircle, CheckCircle, RefreshCw, X } from 'lucide-react';
import { toast } from '../ui/simple-toaster';
import { api, apiFormData, apiBlob } from '../../utils/api';

const DataImportExportPanel = ({ drivers = [] }) => {
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [exportOptions, setExportOptions] = useState({
    type: 'segments',
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
    driverIds: []
  });
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.name.endsWith('.csv')) {
        toast.error('Please select a CSV file');
        return;
      }
      setSelectedFile(file);
      setImportResults(null);
    }
  };

  const handleImport = async () => {
    if (!selectedFile) {
      toast.error('Please select a file first');
      return;
    }

    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const result = await apiFormData('/data/import', formData);
      setImportResults(result);
      toast.success(`Imported ${result.imported_count} records`);
    } catch (err) {
      toast.error('Import failed: ' + err.message);
    } finally {
      setImporting(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams({
        type: exportOptions.type,
        start_date: exportOptions.startDate,
        end_date: exportOptions.endDate
      });
      
      if (exportOptions.driverIds.length > 0) {
        exportOptions.driverIds.forEach(id => params.append('driver_ids', id));
      }

      const blob = await apiBlob(`/data/export?${params}`, { method: 'GET' });
      
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `lawd_${exportOptions.type}_${exportOptions.startDate}_${exportOptions.endDate}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('Export downloaded');
    } catch (err) {
      toast.error('Export failed: ' + err.message);
    } finally {
      setExporting(false);
    }
  };

  const downloadTemplate = () => {
    const template = `date,driver_email,start_time,end_time,segment_type,vehicle_rego,notes
2024-01-15,mike.smith@lawd.com.au,06:00,10:00,WORK,ABC123,Morning deliveries
2024-01-15,mike.smith@lawd.com.au,10:00,10:30,REST,,Rest break
2024-01-15,mike.smith@lawd.com.au,10:30,14:00,WORK,ABC123,Afternoon deliveries
2024-01-15,mike.smith@lawd.com.au,14:00,14:15,REST,,Short break
2024-01-15,mike.smith@lawd.com.au,14:15,18:00,WORK,ABC123,Final runs
2024-01-15,mike.smith@lawd.com.au,18:00,06:00,REST,,Night rest

# Instructions:
# - date: YYYY-MM-DD format
# - driver_email: Must match existing driver
# - start_time/end_time: HH:MM (24-hour format)
# - segment_type: WORK or REST
# - vehicle_rego: Required for WORK segments
# - notes: Optional description`;

    const blob = new Blob([template], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'lawd_import_template.csv';
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
    toast.success('Template downloaded');
  };

  return (
    <div className="space-y-6">
      {/* Import Section */}
      <div className="bg-slate-800/50 rounded-2xl border border-slate-700 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-emerald-500/20 rounded-xl flex items-center justify-center">
            <Upload className="w-5 h-5 text-emerald-400" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-white">Import Work Diary Data</h2>
            <p className="text-slate-400 text-sm">Upload historical work diary records from CSV</p>
          </div>
        </div>

        <div className="space-y-4">
          {/* Template Download */}
          <div className="flex items-center gap-4 p-4 bg-slate-700/50 rounded-xl">
            <FileText className="w-5 h-5 text-slate-400" />
            <div className="flex-1">
              <p className="text-white text-sm font-medium">Need a template?</p>
              <p className="text-slate-400 text-xs">Download our CSV template to ensure correct formatting</p>
            </div>
            <button
              onClick={downloadTemplate}
              className="px-4 py-2 bg-slate-600 text-white rounded-lg hover:bg-slate-500 text-sm flex items-center gap-2"
              data-testid="download-template-btn"
            >
              <Download className="w-4 h-4" />
              Template
            </button>
          </div>

          {/* File Upload */}
          <div 
            className={`border-2 border-dashed rounded-xl p-8 text-center transition ${
              selectedFile ? 'border-emerald-500 bg-emerald-500/10' : 'border-slate-600 hover:border-slate-500'
            }`}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileSelect}
              className="hidden"
              data-testid="file-input"
            />
            
            {selectedFile ? (
              <div className="flex items-center justify-center gap-3">
                <FileText className="w-8 h-8 text-emerald-400" />
                <div className="text-left">
                  <p className="text-white font-medium">{selectedFile.name}</p>
                  <p className="text-slate-400 text-sm">{(selectedFile.size / 1024).toFixed(1)} KB</p>
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); setSelectedFile(null); setImportResults(null); }}
                  className="p-1 hover:bg-slate-700 rounded"
                >
                  <X className="w-5 h-5 text-slate-400" />
                </button>
              </div>
            ) : (
              <>
                <Upload className="w-12 h-12 text-slate-500 mx-auto mb-3" />
                <p className="text-white font-medium">Click to select CSV file</p>
                <p className="text-slate-400 text-sm mt-1">or drag and drop</p>
              </>
            )}
          </div>

          {/* Import Button */}
          {selectedFile && (
            <button
              onClick={handleImport}
              disabled={importing}
              className="w-full px-4 py-3 bg-emerald-500 text-white rounded-xl font-semibold hover:bg-emerald-600 disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="import-btn"
            >
              {importing ? (
                <>
                  <RefreshCw className="w-5 h-5 animate-spin" />
                  Importing...
                </>
              ) : (
                <>
                  <Upload className="w-5 h-5" />
                  Import Data
                </>
              )}
            </button>
          )}

          {/* Import Results */}
          {importResults && (
            <div className={`p-4 rounded-xl ${importResults.errors?.length > 0 ? 'bg-amber-500/10 border border-amber-500/30' : 'bg-emerald-500/10 border border-emerald-500/30'}`}>
              <div className="flex items-center gap-2 mb-2">
                {importResults.errors?.length > 0 ? (
                  <AlertCircle className="w-5 h-5 text-amber-400" />
                ) : (
                  <CheckCircle className="w-5 h-5 text-emerald-400" />
                )}
                <span className="text-white font-medium">Import Complete</span>
              </div>
              <div className="text-sm text-slate-300 space-y-1">
                <p>Imported: {importResults.imported_count} records</p>
                <p>Skipped: {importResults.skipped_count || 0} records</p>
                {importResults.errors?.length > 0 && (
                  <div className="mt-2">
                    <p className="text-amber-400 font-medium">Errors:</p>
                    <ul className="list-disc list-inside text-slate-400">
                      {importResults.errors.slice(0, 5).map((err) => (
                        <li key={err}>{err}</li>
                      ))}
                      {importResults.errors.length > 5 && (
                        <li>...and {importResults.errors.length - 5} more</li>
                      )}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Export Section */}
      <div className="bg-slate-800/50 rounded-2xl border border-slate-700 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-blue-500/20 rounded-xl flex items-center justify-center">
            <Download className="w-5 h-5 text-blue-400" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-white">Export Data</h2>
            <p className="text-slate-400 text-sm">Download work diary data for auditors or backups</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm text-slate-400 mb-1">Data Type</label>
            <select
              value={exportOptions.type}
              onChange={(e) => setExportOptions(prev => ({ ...prev, type: e.target.value }))}
              className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
              data-testid="export-type"
            >
              <option value="segments">Work/Rest Segments</option>
              <option value="boundary_crossings">Compliance Events</option>
              <option value="journeys">Journey Plans</option>
            </select>
          </div>

          <div>
            <label className="block text-sm text-slate-400 mb-1">Drivers (optional)</label>
            <select
              multiple
              value={exportOptions.driverIds}
              onChange={(e) => setExportOptions(prev => ({
                ...prev,
                driverIds: Array.from(e.target.selectedOptions, opt => opt.value)
              }))}
              className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white h-20"
              data-testid="export-drivers"
            >
              {drivers.map(d => (
                <option key={d.driver_id} value={d.driver_id}>
                  {d.users?.first_name} {d.users?.last_name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm text-slate-400 mb-1">Start Date</label>
            <input
              type="date"
              value={exportOptions.startDate}
              onChange={(e) => setExportOptions(prev => ({ ...prev, startDate: e.target.value }))}
              className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
              data-testid="export-start-date"
            />
          </div>

          <div>
            <label className="block text-sm text-slate-400 mb-1">End Date</label>
            <input
              type="date"
              value={exportOptions.endDate}
              onChange={(e) => setExportOptions(prev => ({ ...prev, endDate: e.target.value }))}
              className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
              data-testid="export-end-date"
            />
          </div>
        </div>

        <button
          onClick={handleExport}
          disabled={exporting}
          className="mt-4 w-full px-4 py-3 bg-blue-500 text-white rounded-xl font-semibold hover:bg-blue-600 disabled:opacity-50 flex items-center justify-center gap-2"
          data-testid="export-btn"
        >
          {exporting ? (
            <>
              <RefreshCw className="w-5 h-5 animate-spin" />
              Exporting...
            </>
          ) : (
            <>
              <Download className="w-5 h-5" />
              Export to CSV
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default DataImportExportPanel;
